/**
 * Seed Consolidator
 *
 * Authoritative data loader for the learning system.
 * Imports 10 module-based seed files and validates against Master Taxonomy.
 *
 * Part of the Content domain (DDD Fase 3).
 * Moved from src/data/seedConsolidator.ts in R26.
 * 10 modules: GET, REK, MET, TIJD, GELD, SWW, SPR, TAA, STU, TOPO.
 *
 * Features:
 * - Validates all questions against taxonomy
 * - Enforces tier consistency (tier must match taxonomy)
 * - Auto-corrects skill/subSkill mismatches with warnings
 * - Produces deterministic SeedCollection maps
 */

import { ModuleId, Question, QuestionCore, QuestionType } from '../types'
import { MODULE_ID_TO_CODE, MODULE_CODE_TO_ID } from '../modules'
import { type VisualAid } from '../../visuals'
import {
  isValidFamilyId,
  getFamilyDefinition,
  getAuthoritativeTier,
} from '../taxonomy'
import {
  debug,
  warn,
  error as logError,
  group as logGroup,
  groupEnd as logGroupEnd,
} from '../../utils/logger'
import {
  checkVisualRegistrySync,
  getRegisteredVisualTypes,
} from '../../governance'

// ============================================================
// Import New Seed Files
// ============================================================

import {
  seedSWW,
  seedSPR,
  seedGET,
  seedREK,
  seedMET,
  seedTIJD,
  seedGELD,
  seedTOPO,
  seedTAA,
  seedSTU,
} from '../seeds'

// ============================================================
// Configuration
// ============================================================

/**
 * STRICT mode: When enabled, validation errors will throw instead of auto-correcting.
 * ENABLED BY DEFAULT to enforce data quality (2026-02-18).
 *
 * Enforces:
 * - Skill values must match taxonomy definitions
 * - SubSkill values must match taxonomy definitions
 *
 * Note: Tier mismatches are always auto-corrected from taxonomy (tier is authoritative).
 */
const STRICT_MODE = true

// ============================================================
// Types
// ============================================================

export interface SeedQuestionSourceMetadata {
  __sourceFile?: string
  __sourceSeedKey?: string
}
export interface SeedQuestion {
  // Identity
  id: string
  module: ModuleId
  familyId: string // REQUIRED — canonical 4.1 familyId

  // Tier is authoritative
  tier: number
  levelCode: string
  group: number

  // Content
  question: string
  questionType: string
  context?: string
  options: string[]
  correctAnswer: number

  // Skills — REQUIRED (enforced from taxonomy)
  skill: string // REQUIRED
  subSkill: string // REQUIRED

  // Taxonomy fields
  conceptId?: string
  variantIndex?: number

  // CITO
  citoDifficulty?: number
  citoDiscrimination?: number
  isAnchor?: boolean

  // Universal data payload
  data?: Record<string, unknown>

  // Canonical feedback
  feedback?: Question['feedback']

  // Visual Aids (canonical system — STAGE 3)
  stimulusAids?: VisualAid[]
  supportAids?: VisualAid[]

  // Dev diagnostics provenance
  __sourceFile?: string
  __sourceSeedKey?: string
}
export interface SeedCollection {
  module: ModuleId
  moduleCode: string
  version: string
  taxonomyVersion: string
  questions: SeedQuestion[]
}

// ============================================================
// Module Code Mapping — imported from canonical source (modules.ts)
// ============================================================

/** @deprecated Use MODULE_ID_TO_CODE from content/modules.ts directly */
export const MODULE_CODES = MODULE_ID_TO_CODE

// ============================================================
// Tier → Group Mapping
// ============================================================

function getGroupFromTier(tier: number): number {
  if (tier <= 6) return 6
  if (tier <= 12) return 7
  if (tier <= 16) return 8
  return 8
}
const DEPRECATED_SEED_KEYS = [
  'visualAid',
  'explanation',
  'explanationPerOption',
  'explanationCorrect',
  'distractorExplanations',
]
const DEPRECATED_FEEDBACK_KEYS = [
  'feedback.explanation',
  'feedback.explanationPerOption',
  'feedback.stimulusAids',
  'feedback.supportAids',
]
function assertCanonicalSeedPayload(raw: Record<string, unknown>): void {
  const deprecated: string[] = []
  const feedback = (raw.feedback ?? {}) as Record<string, unknown>
  DEPRECATED_SEED_KEYS.forEach((key) => {
    if (key in raw) deprecated.push(key)
  })
  DEPRECATED_FEEDBACK_KEYS.forEach((key) => {
    const nested = key.split('.')[1]
    if (nested && nested in feedback) deprecated.push(key)
  })
  if (deprecated.length > 0) {
    throw new Error(
      `Question ${String(raw.id ?? 'unknown')} contains deprecated keys: ${deprecated.join(', ')}.`,
    )
  }
}

// ============================================================
// Normalize Raw Seed → SeedQuestion
// ============================================================

function normalizeSeedQuestion(
  raw: Record<string, unknown>,
  moduleId: ModuleId,
  capturedWarnings?: string[],
): SeedQuestion | null {
  // Validate familyId
  const rawFamilyId = raw.familyId
  if (!rawFamilyId) {
    logError(`SEED ERROR: Question ${raw.id} is missing familyId`)
    return null
  }
  if (!isValidFamilyId(rawFamilyId)) {
    logError(
      `SEED ERROR: Question ${raw.id} has invalid familyId "${rawFamilyId}"`,
    )
    return null
  }

  // Look up the taxonomy-authoritative tier for this familyId
  const taxonomyTier = getAuthoritativeTier(rawFamilyId)
  const familyDef = getFamilyDefinition(rawFamilyId)

  // Validate and enforce tier from taxonomy
  let actualTier: number
  const rawTier = raw.tier
  if (typeof rawTier !== 'number' || rawTier < 1) {
    if (taxonomyTier !== undefined) {
      const msg = `SEED TIER-LOCK: Question ${raw.id} has invalid tier (${rawTier}), auto-corrected to taxonomy tier ${taxonomyTier} for family ${rawFamilyId}`
      warn(msg)
      capturedWarnings?.push(msg)
      actualTier = taxonomyTier
    } else {
      logError(
        `SEED ERROR: Question ${raw.id} has invalid tier (${rawTier}) and no taxonomy fallback`,
      )
      return null
    }
  } else if (rawTier > 16) {
    if (taxonomyTier !== undefined) {
      const msg = `SEED TIER-LOCK: Question ${raw.id} has out-of-range tier ${rawTier}, auto-corrected to taxonomy tier ${taxonomyTier} for family ${rawFamilyId}`
      warn(msg)
      capturedWarnings?.push(msg)
      actualTier = taxonomyTier
    } else {
      logError(
        `SEED ERROR: Question ${raw.id} has out-of-range tier ${rawTier} (valid: 1-16)`,
      )
      return null
    }
  } else if (taxonomyTier !== undefined && rawTier !== taxonomyTier) {
    const msg =
      `SEED TIER-LOCK: Question ${raw.id} (family ${rawFamilyId}) has tier ${rawTier} but taxonomy defines tier ${taxonomyTier}. ` +
      `Auto-corrected to ${taxonomyTier}. [Expected: ${familyDef?.levelCode || '?'}]`
    warn(msg)
    capturedWarnings?.push(msg)
    actualTier = taxonomyTier
  } else {
    actualTier = rawTier
  }
  const group = getGroupFromTier(actualTier)

  // Derive levelCode from tier (canonical, not from raw data)
  const levelCode = familyDef?.levelCode ?? `T${actualTier}`

  // Validate skill/subSkill presence
  if (!raw.skill) {
    logError(`SEED ERROR: Question ${raw.id} is missing skill`)
    return null
  }
  if (!raw.subSkill) {
    logError(`SEED ERROR: Question ${raw.id} is missing subSkill`)
    return null
  }
  try {
    assertCanonicalSeedPayload(raw as Record<string, unknown>)
  } catch (err) {
    logError(`SEED ERROR: Question ${raw.id} has non-canonical payload:`, err)
    return null
  }
  if (typeof raw.questionType !== 'string' || !raw.questionType.trim()) {
    logError(
      `SEED ERROR: Question ${raw.id} is missing required canonical questionType`,
    )
    return null
  }

  // Enforce skill/subSkill from taxonomy
  let actualSkill = raw.skill
  let actualSubSkill = raw.subSkill
  if (familyDef) {
    if (raw.skill !== familyDef.skill) {
      const message = `SEED SKILL-LOCK: Question ${raw.id} (family ${rawFamilyId}) has skill "${raw.skill}" but taxonomy defines "${familyDef.skill}". Auto-corrected.`
      if (STRICT_MODE) {
        logError(message)
        return null
      } else {
        warn(message)
        capturedWarnings?.push(message)
        actualSkill = familyDef.skill
      }
    }

    if (raw.subSkill !== familyDef.subSkill) {
      const message = `SEED SUBSKILL-LOCK: Question ${raw.id} (family ${rawFamilyId}) has subSkill "${raw.subSkill}" but taxonomy defines "${familyDef.subSkill}". Auto-corrected.`
      if (STRICT_MODE) {
        logError(message)
        return null
      } else {
        warn(message)
        capturedWarnings?.push(message)
        actualSubSkill = familyDef.subSkill
      }
    }
  }
  return {
    // Identity
    id: raw.id,
    module: moduleId,
    familyId: rawFamilyId,
    // Tier is authoritative (from taxonomy, not raw)
    tier: actualTier,
    levelCode,
    group,
    // Content
    question: raw.question,
    questionType: raw.questionType,
    context: raw.context,
    options: raw.options ?? [],
    correctAnswer: raw.correctAnswer ?? 0,
    // Skills — ENFORCED from taxonomy
    skill: actualSkill,
    subSkill: actualSubSkill,
    // Taxonomy fields
    conceptId: raw.conceptId,
    variantIndex: raw.variantIndex ?? 1,
    // CITO
    citoDifficulty: raw.citoDifficulty,
    citoDiscrimination: raw.citoDiscrimination,
    isAnchor: raw.isAnchor,
    // Universal data payload
    data: raw.data,
    // Canonical feedback
    feedback: raw.feedback,
    // Visual Aids (canonical system — STAGE 3)
    stimulusAids: raw.stimulusAids,
    supportAids: raw.supportAids,
    __sourceFile: raw.__sourceFile,
    __sourceSeedKey: raw.__sourceSeedKey,
  }
}

// ============================================================
// Build Seed Collections
// ============================================================

function buildSeedCollections(): Map<ModuleId, SeedCollection> {
  const collections = new Map<ModuleId, SeedCollection>()
  const seenIds = new Set<string>()
  const errors: string[] = []
  const warnings: string[] = []
  const seedFiles = [
    {
      key: 'seed_SWW',
      sourceFile: 'src/content/seeds/SWW/index.ts',
      data: seedSWW,
    },
    {
      key: 'seed_SPR',
      sourceFile: 'src/content/seeds/SPR/index.ts',
      data: seedSPR,
    },
    {
      key: 'seed_GET',
      sourceFile: 'src/content/seeds/GET/index.ts',
      data: seedGET,
    },
    {
      key: 'seed_REK',
      sourceFile: 'src/content/seeds/REK/index.ts',
      data: seedREK,
    },
    {
      key: 'seed_MET',
      sourceFile: 'src/content/seeds/MET/index.ts',
      data: seedMET,
    },
    {
      key: 'seed_TIJD',
      sourceFile: 'src/content/seeds/TIJD/index.ts',
      data: seedTIJD,
    },
    {
      key: 'seed_GELD',
      sourceFile: 'src/content/seeds/GELD/index.ts',
      data: seedGELD,
    },
    {
      key: 'seed_TOPO',
      sourceFile: 'src/content/seeds/TOPO/index.ts',
      data: seedTOPO,
    },
    {
      key: 'seed_TAA',
      sourceFile: 'src/content/seeds/TAA/index.ts',
      data: seedTAA,
    },
    {
      key: 'seed_STU',
      sourceFile: 'src/content/seeds/STU/index.ts',
      data: seedSTU,
    },
  ]

  let rejectedCount = 0

  for (const seedSource of seedFiles) {
    const seedFile = seedSource.data
    const moduleCode = seedFile.moduleCode
    const moduleId = MODULE_CODE_TO_ID[moduleCode]
    if (!moduleId) {
      errors.push(`Unknown module code: ${moduleCode}`)
      continue
    }
    const collection: SeedCollection = {
      module: moduleId,
      moduleCode,
      version: seedFile.version,
      taxonomyVersion: seedFile.taxonomyVersion,
      questions: [],
    }
    for (const rawQ of seedFile.questions) {
      const withSource = {
        ...rawQ,
        __sourceFile: rawQ.__sourceFile ?? seedSource.sourceFile,
        __sourceSeedKey: seedSource.key,
      }
      // Deduplicate
      if (seenIds.has(rawQ.id)) {
        errors.push(`Duplicate question ID: ${rawQ.id}`)
        rejectedCount++
        continue
      }
      seenIds.add(rawQ.id)
      const seed = normalizeSeedQuestion(withSource, moduleId, warnings)
      if (!seed) {
        // normalizeSeedQuestion already logged the error
        rejectedCount++
        continue
      }
      collection.questions.push(seed)
    }

    // Sort by tier then ID
    collection.questions.sort((a, b) => {
      if (a.tier !== b.tier) return a.tier - b.tier
      return a.id.localeCompare(b.id)
    })
    collections.set(moduleId, collection)
  }

  // Store build-time metadata for consolidation report
  _buildErrors = errors
  _buildWarnings = warnings
  _buildRejectedCount = rejectedCount

  // Log results
  if (import.meta.env?.MODE === 'development') {
    logGroup('🌱 Seed Consolidator v4.2')
    debug(`Loaded ${seedFiles.length} seed files`)
    debug(`Total questions: ${seenIds.size}`)
    debug(`Rejected questions: ${rejectedCount}`)
    debug(`STRICT mode: ${STRICT_MODE ? 'ENABLED' : 'DISABLED'}`)
    for (const [moduleId, collection] of collections) {
      const families = new Set(collection.questions.map((q) => q.familyId))
      debug(
        `  ${collection.moduleCode} (${moduleId}): ${collection.questions.length} questions, ${families.size} families`,
      )
    }
    if (errors.length > 0) {
      logError(`${errors.length} errors during seed loading:`)
      errors.forEach((e) => logError(`  ${e}`))
    }
    logGroupEnd()
  }
  return collections
}

// Build-time metadata (captured during buildSeedCollections, consumed by getConsolidationReport)
let _buildErrors: string[] = []
let _buildWarnings: string[] = []
let _buildRejectedCount = 0

// ============================================================
// Validation (for dev panels)
// ============================================================

import { TAXONOMY } from '../taxonomy'

/**
 * Validate the seed system against Master Taxonomy 4.1.
 * Checks for 100% familyId coverage.
 */
export function validateSeedCoverage(): {
  isValid: boolean
  totalFamilies: number
  coveredFamilies: number
  missingFamilies: string[]
  extraFamilies: string[]
  duplicateFamilies: string[]
  totalQuestions: number
  questionsByModule: Record<string, number>
  errors: string[]
  warnings: string[]
} {
  const report = {
    isValid: false,
    totalFamilies: TAXONOMY.length,
    coveredFamilies: 0,
    missingFamilies: [] as string[],
    extraFamilies: [] as string[],
    duplicateFamilies: [] as string[],
    totalQuestions: 0,
    questionsByModule: {} as Record<string, number>,
    errors: [] as string[],
    warnings: [] as string[],
  }
  const taxonomyFamilyIds = new Set(TAXONOMY.map((f) => f.familyId))
  const seedFamilyIds = new Set<string>()
  const familyIdCounts = new Map<string, number>()
  for (const collection of SEED_COLLECTIONS.values()) {
    report.questionsByModule[collection.moduleCode] =
      collection.questions.length
    report.totalQuestions += collection.questions.length
    for (const q of collection.questions) {
      seedFamilyIds.add(q.familyId)
      familyIdCounts.set(q.familyId, (familyIdCounts.get(q.familyId) ?? 0) + 1)
    }
  }
  for (const familyId of taxonomyFamilyIds) {
    if (seedFamilyIds.has(familyId)) {
      report.coveredFamilies++
    } else {
      report.missingFamilies.push(familyId)
    }
  }
  for (const familyId of seedFamilyIds) {
    if (!taxonomyFamilyIds.has(familyId)) {
      report.extraFamilies.push(familyId)
    }
  }
  for (const [familyId, count] of familyIdCounts) {
    if (count > 1) {
      report.duplicateFamilies.push(familyId)
    }
  }
  if (report.missingFamilies.length > 0) {
    report.errors.push(
      `Missing ${report.missingFamilies.length} familyIds from taxonomy`,
    )
  }
  if (report.extraFamilies.length > 0) {
    report.warnings.push(
      `${report.extraFamilies.length} familyIds in seeds not in taxonomy`,
    )
  }
  report.isValid =
    report.missingFamilies.length === 0 && report.extraFamilies.length === 0
  return report
}

/**
 * Validate individual seed questions for structural correctness.
 */
export function validateSeedQuestionsLocal(): {
  valid: number
  invalid: number
  issues: {
    id: string
    problems: string[]
  }[]
} {
  const issues: {
    id: string
    problems: string[]
  }[] = []
  let valid = 0
  let invalid = 0
  for (const collection of SEED_COLLECTIONS.values()) {
    for (const q of collection.questions) {
      const problems: string[] = []
      if (!q.id) problems.push('Missing id')
      if (!q.module) problems.push('Missing module')
      if (!q.familyId) problems.push('Missing familyId')
      if (!q.question) problems.push('Missing question text')
      if (!q.skill) problems.push('Missing skill')
      if (!q.subSkill) problems.push('Missing subSkill')
      if (q.tier < 1 || q.tier > 16) problems.push(`Invalid tier: ${q.tier}`)
      if (!q.feedback?.explanationText?.trim()) {
        problems.push('Missing canonical feedback.explanationText')
      }
      const qType = q.questionType
      if (qType === 'multiple_choice') {
        if (!q.options || q.options.length < 2)
          problems.push('MC needs at least 2 options')
        if (
          q.correctAnswer === undefined ||
          q.correctAnswer < 0 ||
          q.correctAnswer >= (q.options?.length ?? 0)
        )
          problems.push('Invalid correctAnswer index')
        const distractorExplanations = q.feedback?.distractorExplanations
        if (!distractorExplanations) {
          problems.push(
            'Missing canonical feedback.distractorExplanations for MCQ option coverage',
          )
        } else if (q.options && q.options.length > 0) {
          q.options.forEach((_, optionIndex) => {
            const explanation = distractorExplanations[optionIndex.toString()]
            if (typeof explanation !== 'string' || explanation.trim() === '') {
              problems.push(
                `Missing canonical feedback.distractorExplanations[${optionIndex}]`,
              )
            }
          })
        }

        // === NEW: Question quality checks (Part 3) ===

        // Check for duplicate options
        if (q.options && q.options.length > 0) {
          const normalizedOptions = q.options.map((o: string) =>
            o.trim().toLowerCase(),
          )
          const uniqueOptions = new Set(normalizedOptions)
          if (uniqueOptions.size < normalizedOptions.length) {
            problems.push('Duplicate options detected')
          }
        }

        // Check that no distractor is identical to the correct answer
        if (
          q.options &&
          q.correctAnswer !== undefined &&
          q.correctAnswer >= 0
        ) {
          const correctText = (q.options[q.correctAnswer] || '')
            .trim()
            .toLowerCase()
          q.options.forEach((opt: string, idx: number) => {
            if (
              idx !== q.correctAnswer &&
              opt.trim().toLowerCase() === correctText
            ) {
              problems.push(
                `Distractor at index ${idx} is identical to correct answer`,
              )
            }
          })
        }

        // Check for empty options
        if (q.options) {
          q.options.forEach((opt: string, idx: number) => {
            if (!opt || opt.trim() === '') {
              problems.push(`Empty option at index ${idx}`)
            }
          })
        }
      }

      // Interactive type validation: require data.{type}
      // NOTE: Some types use canonical nested format (data.clock = {...})
      // while others use flat format (data = { acceptedAnswers: [...] }).
      // We accept EITHER: nested key matching type name, or flat data with any keys.
      const hasDataPayload = (type: string) =>
        q.data &&
        typeof q.data === 'object' &&
        (type in q.data || Object.keys(q.data).length > 0)
      if (qType === 'clock' && !hasDataPayload('clock'))
        problems.push('Clock type needs data.clock')
      if (qType === 'table_reading' && !hasDataPayload('table_reading'))
        problems.push('Table type needs data.table_reading')
      if (qType === 'bar_chart' && !hasDataPayload('bar_chart'))
        problems.push('Bar chart type needs data.bar_chart')
      if (qType === 'schedule_reading' && !hasDataPayload('schedule_reading'))
        problems.push('Schedule type needs data.schedule_reading')
      if (qType === 'fill_in' && !hasDataPayload('fill_in'))
        problems.push('Fill-in type needs data.fill_in')
      if (qType === 'ratio_table' && !hasDataPayload('ratio_table'))
        problems.push('Ratio table type needs data.ratio_table')
      if (qType === 'line_chart' && !hasDataPayload('line_chart'))
        problems.push('Line chart type needs data.line_chart')
      if (qType === 'number_sequence' && !hasDataPayload('number_sequence'))
        problems.push('Number sequence type needs data.number_sequence')
      if (qType === 'ordering' && !hasDataPayload('ordering'))
        problems.push('Ordering type needs data.ordering')
      if (qType === 'matching' && !hasDataPayload('matching'))
        problems.push('Matching type needs data.matching')
      if (qType === 'money' && !hasDataPayload('money'))
        problems.push('Money type needs data.money')
      if (qType === 'drag_drop_fill' && !hasDataPayload('drag_drop_fill'))
        problems.push('Drag-drop-fill type needs data.drag_drop_fill')
      if (qType === 'text_highlight' && !hasDataPayload('text_highlight'))
        problems.push('Text-highlight type needs data.text_highlight')
      if (qType === 'pie_chart' && !hasDataPayload('pie_chart'))
        problems.push('Pie chart type needs data.pie_chart')
      if (qType === 'column_arithmetic' && !hasDataPayload('column_arithmetic'))
        problems.push('Column arithmetic type needs data.column_arithmetic')

      // Verify exactly one correct answer for all types with options
      if (q.options && q.options.length > 0) {
        if (q.correctAnswer === undefined || q.correctAnswer === null) {
          problems.push('Missing correctAnswer for question with options')
        }
      }

      // citoDifficulty range validation (0.15–0.75)
      if (q.citoDifficulty !== undefined) {
        if (q.citoDifficulty < 0.15 || q.citoDifficulty > 0.75) {
          problems.push(
            `citoDifficulty ${q.citoDifficulty} out of valid range (0.15–0.75)`,
          )
        }
      }

      // conceptId presence check
      if (
        !q.conceptId ||
        (typeof q.conceptId === 'string' && !q.conceptId.trim())
      ) {
        problems.push('Missing conceptId')
      }

      if (problems.length > 0) {
        invalid++
        issues.push({
          id: q.id,
          problems,
        })
      } else {
        valid++
      }
    }
  }
  return {
    valid,
    invalid,
    issues,
  }
}

/**
 * Get the question type distribution across all seeds.
 */
export function getSeedQuestionTypeDistribution(): Record<string, number> {
  const dist: Record<string, number> = {}
  for (const collection of SEED_COLLECTIONS.values()) {
    for (const q of collection.questions) {
      const type = q.questionType
      dist[type] = (dist[type] ?? 0) + 1
    }
  }
  return dist
}

/**
 * Validate cross-seed duplicate question IDs.
 *
 * RESOLVES: SYSTEM_PLAN_OF_APPROACH item #3 (Cross-seed duplicate question ID check)
 *
 * Checks for question IDs that appear in multiple seed files.
 * The buildSeedCollections() function already deduplicates at load time,
 * but this provides a diagnostic report for the developer panel.
 */
export function validateCrossSeedDuplicateIds(): {
  hasDuplicates: boolean
  totalQuestions: number
  uniqueIds: number
  duplicates: {
    id: string
    occurrences: { module: string; sourceFile: string }[]
  }[]
} {
  const idOccurrences = new Map<
    string,
    { module: string; sourceFile: string }[]
  >()

  for (const collection of SEED_COLLECTIONS.values()) {
    for (const q of collection.questions) {
      if (!idOccurrences.has(q.id)) {
        idOccurrences.set(q.id, [])
      }
      idOccurrences.get(q.id)!.push({
        module: collection.moduleCode,
        sourceFile: q.__sourceFile ?? 'unknown',
      })
    }
  }

  const duplicates: {
    id: string
    occurrences: { module: string; sourceFile: string }[]
  }[] = []
  let totalQuestions = 0

  for (const [id, occurrences] of idOccurrences) {
    totalQuestions += occurrences.length
    if (occurrences.length > 1) {
      duplicates.push({ id, occurrences })
    }
  }

  return {
    hasDuplicates: duplicates.length > 0,
    totalQuestions,
    uniqueIds: idOccurrences.size,
    duplicates,
  }
}

// ============================================================
// Exports
// ============================================================

/** All 10 seed collections, built once at module load time */
export const SEED_COLLECTIONS: Map<ModuleId, SeedCollection> =
  buildSeedCollections()

// ─── §4.2 Consolidation Report ───────────────────────────────────────────────

/**
 * Structured consolidation report — surfaces hidden load errors in admin/dev.
 *
 * RESOLVES: SYSTEM_STABILIZATION_BLUEPRINT §4.2
 */
export interface ConsolidationReport {
  /** Total seed files processed */
  totalSeedFiles: number
  /** Total questions successfully loaded */
  totalQuestions: number
  /** Questions rejected during normalisation */
  rejectedQuestions: number
  /** Per-module breakdown */
  modules: {
    moduleCode: string
    moduleId: ModuleId
    questionCount: number
    familyCount: number
    tierRange: [number, number]
  }[]
  /** Family coverage: familyId → question count */
  familyCoverage: Record<string, number>
  /** Errors encountered during load */
  errors: string[]
  /** Warnings encountered during load */
  warnings: string[]
  /** Visual registry sync status */
  visualRegistrySync: {
    inSync: boolean
    missingFromGovernance: string[]
    missingFromRegistry: string[]
  }
  /** Timestamp */
  generatedAt: string
}

/**
 * Generate a structured consolidation report from the loaded seed data.
 * Safe to call at any time — reads from the already-built SEED_COLLECTIONS.
 */
export function getConsolidationReport(): ConsolidationReport {
  const modules: ConsolidationReport['modules'] = []
  const familyCoverage: Record<string, number> = {}
  let totalQuestions = 0

  for (const [moduleId, collection] of SEED_COLLECTIONS) {
    const families = new Set<string>()
    let minTier = 16
    let maxTier = 1

    for (const q of collection.questions) {
      totalQuestions++
      families.add(q.familyId)
      familyCoverage[q.familyId] = (familyCoverage[q.familyId] ?? 0) + 1
      if (q.tier < minTier) minTier = q.tier
      if (q.tier > maxTier) maxTier = q.tier
    }

    modules.push({
      moduleCode: collection.moduleCode,
      moduleId,
      questionCount: collection.questions.length,
      familyCount: families.size,
      tierRange: [minTier, maxTier],
    })
  }

  // Visual registry sync check (§5.4)
  let visualSync: ConsolidationReport['visualRegistrySync'] = {
    inSync: true,
    missingFromGovernance: [],
    missingFromRegistry: [],
  }
  try {
    // Collect visual types referenced in seeds
    const seedVisualTypes = new Set<string>()
    for (const collection of SEED_COLLECTIONS.values()) {
      for (const q of collection.questions) {
        for (const aid of q.stimulusAids ?? []) {
          if (aid?.type) seedVisualTypes.add(aid.type)
        }
        for (const aid of q.supportAids ?? []) {
          if (aid?.type) seedVisualTypes.add(aid.type)
        }
      }
    }
    const syncReport = checkVisualRegistrySync([...seedVisualTypes])
    visualSync = {
      inSync: syncReport.inSync,
      missingFromGovernance: syncReport.missingFromGovernance,
      missingFromRegistry: syncReport.missingFromRegistry,
    }
  } catch {
    // Sync check is non-critical — don't break consolidation
  }

  return {
    totalSeedFiles: SEED_COLLECTIONS.size,
    totalQuestions,
    rejectedQuestions: _buildRejectedCount,
    modules,
    familyCoverage,
    errors: [..._buildErrors],
    warnings: [..._buildWarnings],
    visualRegistrySync: visualSync,
    generatedAt: new Date().toISOString(),
  }
}

/** Get a specific module's seed collection */
export function getSeedCollection(
  moduleId: ModuleId,
): SeedCollection | undefined {
  return SEED_COLLECTIONS.get(moduleId)
}

/** Get all seed questions across all modules */
export function getAllSeedQuestions(): SeedQuestion[] {
  const all: SeedQuestion[] = []
  for (const collection of SEED_COLLECTIONS.values()) {
    all.push(...collection.questions)
  }
  return all
}

/** Get seed questions for a specific module */
export function getModuleSeedQuestions(moduleId: ModuleId): SeedQuestion[] {
  return SEED_COLLECTIONS.get(moduleId)?.questions ?? []
}

/** Get seed questions filtered by tier */
export function getSeedQuestionsByTier(
  moduleId: ModuleId,
  tier: number,
): SeedQuestion[] {
  return getModuleSeedQuestions(moduleId).filter((q) => q.tier === tier)
}

/** Get seed questions filtered by familyId */
export function getSeedQuestionsByFamily(familyId: string): SeedQuestion[] {
  const all: SeedQuestion[] = []
  for (const collection of SEED_COLLECTIONS.values()) {
    for (const q of collection.questions) {
      if (q.familyId === familyId) all.push(q)
    }
  }
  return all
}

/**
 * Convert a SeedQuestion to QuestionCore (the public question type).
 */
function seedToCore(seed: SeedQuestion): QuestionCore {
  return {
    id: seed.id,
    module: seed.module,
    question: seed.question,
    questionType: seed.questionType as QuestionType,
    citoDifficulty: seed.citoDifficulty,
    citoDiscrimination: seed.citoDiscrimination,
    skill: seed.skill,
    subSkill: seed.subSkill,
    familyId: seed.familyId,
    context: seed.context,
    options: seed.options,
    correctAnswer: seed.correctAnswer,
    isAnchor: seed.isAnchor,
    data: seed.data,
    // Canonical feedback + visual aids (v3.5 fix — Problem 34: was missing, causing data loss in diagnostic path)
    feedback: seed.feedback,
    stimulusAids: seed.stimulusAids,
    supportAids: seed.supportAids,
    // Taxonomy-authoritative fields (tier is canonical)
    tier: seed.tier,
    levelCode: seed.levelCode,
    group: seed.group,
    conceptId: seed.conceptId,
    variantIndex: seed.variantIndex,
  }
}

/**
 * Run comprehensive governance validation on all seed questions using the pipeline.
 * Replaces the legacy validateSeedQuestionsReport from seedValidator.ts (Step 3.11).
 */
export async function validateSeedQuestionsGovernanceReport() {
  const { runPipelineBatch } = await import(
    '../../governance/pipeline/orchestrator'
  )
  const allQuestions = getAllSeedQuestions()
  // Convert SeedQuestion → Question shape for pipeline
  const asQuestions = allQuestions.map((q) => ({
    ...q,
    questionType: q.questionType as QuestionType,
  }))
  return runPipelineBatch(asQuestions)
}
export interface DiagnosticQuestionWithSource extends SeedQuestion {
  __sourceFile: string
  __sourceSeedKey?: string
}

/**
 * Dev-only helper: normalized questions enriched with source provenance.
 */
export function getAllQuestionsWithSources(): DiagnosticQuestionWithSource[] {
  return getAllSeedQuestions().map((question) => ({
    ...question,
    __sourceFile: question.__sourceFile ?? 'unknown:source-file-not-available',
  }))
}
